import React, { useState, useRef, useEffect } from "react";
import { X, LifeBuoy, Send, Bug, Lightbulb, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import Markdown from "../lib/Markdown";
import { Logo } from "./Logo";

export default function SelfAIModal({ open, onClose }) {
  const [tab, setTab] = useState("ask"); // ask | bug | idea
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  if (!open) return null;

  const ask = async () => {
    const q = input.trim();
    if (!q || busy) return;
    setMsgs((m) => [...m, { role: "user", content: q }]);
    setInput(""); setBusy(true);
    try {
      const r = await api.selfAiAsk(q);
      setMsgs((m) => [...m, { role: "ai", content: r.reply }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "ai", content: "⚠️ " + (e?.response?.data?.detail || "Self-AI is unavailable right now.") }]);
    } finally { setBusy(false); }
  };

  const sendFeedback = async () => {
    const message = input.trim();
    if (message.length < 3 || busy) return;
    setBusy(true);
    try {
      const r = await api.selfAiFeedback({ type: tab, message });
      toast.success(tab === "bug" ? `Bug recorded (severity: ${r.severity})` : "Idea recorded — thank you!");
      setMsgs((m) => [...m, { role: "system", content: (tab === "bug" ? "🐞 Bug report saved. " : "💡 Feature idea saved. ") + "Our team reviews these for controlled, approved improvements." }]);
      setInput("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save. Please try again.");
    } finally { setBusy(false); }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); tab === "ask" ? ask() : sendFeedback(); } };

  const TABS = [
    { id: "ask", label: "Ask Self AI", icon: LifeBuoy },
    { id: "bug", label: "Report a bug", icon: Bug },
    { id: "idea", label: "Suggest a feature", icon: Lightbulb },
  ];

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" data-testid="self-ai-modal">
      <div className="bg-[#0B0F17] border border-white/10 rounded-2xl w-full max-w-lg h-[85vh] max-h-[640px] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <Logo size={26} />
            <div>
              <div className="font-head font-bold text-white text-sm leading-none">SSG AI · Self AI</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Diagnose · Explain · Improve</div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="close-self-ai"><X size={18} /></button>
        </div>

        <div className="flex gap-1 p-2 border-b border-white/10">
          {TABS.map((t) => {
            const Icon = t.icon; const active = tab === t.id;
            return (
              <button key={t.id} data-testid={`self-ai-tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[12px] font-semibold transition-colors ${active ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "text-slate-400 hover:text-white border border-transparent"}`}>
                <Icon size={13} /> <span className="hidden sm:inline">{t.label}</span>
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="self-ai-thread">
          {msgs.length === 0 && (
            <div className="text-center text-slate-500 text-sm mt-10 px-6">
              {tab === "ask" && "Ask anything about SSG AI — how a feature works, why something isn't behaving, or how to get a result."}
              {tab === "bug" && "Describe what went wrong. We'll classify severity and log it for a controlled fix."}
              {tab === "idea" && "Tell us what would make SSG AI better. Approved ideas feed our roadmap."}
            </div>
          )}
          {msgs.map((m, i) => (
            m.role === "user" ? (
              <div key={i} className="flex justify-end"><div className="bg-slate-800/90 border border-white/10 rounded-2xl rounded-tr-sm px-3.5 py-2.5 max-w-[85%] text-sm">{m.content}</div></div>
            ) : m.role === "system" ? (
              <div key={i} className="flex items-start gap-2 text-[13px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-3 py-2"><CheckCircle2 size={15} className="shrink-0 mt-0.5" /><span>{m.content}</span></div>
            ) : (
              <div key={i} className="flex gap-2"><Logo size={24} className="shrink-0" /><div className="bg-[#111827] border border-white/5 rounded-2xl rounded-tl-sm px-3.5 py-2.5 max-w-[88%] text-sm"><Markdown>{m.content}</Markdown></div></div>
            )
          ))}
          <div ref={endRef} />
        </div>

        <div className="p-3 border-t border-white/10">
          <div className="flex items-end gap-2">
            <textarea data-testid="self-ai-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey} rows={1}
              placeholder={tab === "ask" ? "Ask Self AI…" : tab === "bug" ? "Describe the bug…" : "Describe your idea…"}
              className="flex-1 resize-none bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500/50 max-h-32" />
            <button data-testid="self-ai-send" onClick={tab === "ask" ? ask : sendFeedback} disabled={busy}
              className="p-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
